import Close from "@/assets/icons/Close";
import Edit from "@/assets/icons/Edit";
import InfoLine from "@/assets/icons/InfoLine";
import AppButton from "@/components/shared/AppButton";
import { Box } from "@/components/ui/box";
import { Modal, ModalBackdrop, ModalCloseButton, ModalContent } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { validateLicensePlate } from "@/utils/licensePlateValidation";
import { useT } from "@transifex/react";
import { useState } from "react";
import { TextInput } from "react-native";

interface PlateModalProps {
  isOpen: boolean;
  onClose: () => void;
  licensePlate: string;
  onConfirm: (newPlate?: string) => void;
  type: "confirm" | "currentlyactive" | "remove" | "update";
  nickname?: string;
  isRemoving?: boolean;
}

const PlateModal: React.FC<PlateModalProps> = ({ isOpen, onClose, licensePlate, onConfirm, type, nickname, isRemoving = false }) => {
  const t = useT();
  const [loading, setLoading] = useState(false);
  const [newPlate, setNewPlate] = useState<string>(licensePlate);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSelectPlate = (value: string) => {
    const formattedPlate = value.trim().toLocaleUpperCase().slice(0, 12);
    const errorMessage = validateLicensePlate(formattedPlate);
    setErrorMessage(errorMessage ? t(errorMessage) : null);
    setNewPlate(formattedPlate);
  };

  const renderHeading = () => {
    switch (type) {
      case "confirm":
        return t("Confirm Plate");
      case "currentlyactive":
        return t("Vehicle Currently Parked");
      case "remove":
        return t("Remove Plate");
      case "update":
        return t("Update Plate");
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      onConfirm();
    } finally {
      setLoading(false);
    }
  };

  const renderMessage = () => {
    switch (type) {
      case "confirm":
        return t("Please confirm your license plate is correct before proceeding:");
      case "currentlyactive":
        return t(
          `The vehicle ${nickname}, with the license plate number listed below is currently in an active parking session. Proceeding will cancel the current session and start a new one.`,
        );
      case "remove":
        return t("Please confirm the license plate number below that is being removed:");
      case "update":
        return t("Please update the correct plate:");
    }
  };

  const renderIcon = () => {
    switch (type) {
      case "confirm":
        return <InfoLine height={25} width={25} stroke="bg-typography-900" fill="none" />;
      case "currentlyactive":
        return <InfoLine height={25} width={25} stroke="bg-success-500" fill="none" />;
      case "remove":
        return <InfoLine height={25} width={25} stroke="bg-typography-900" fill="none" />;
      case "update":
        return <Edit height={25} width={25} stroke="none" />;
    }
  };

  const renderButtonLabel = () => {
    switch (type) {
      case "confirm":
        return t("Confirm Plate");
      case "currentlyactive":
        return t("Start New Session");
      case "remove":
        return t("Remove Plate");
      case "update":
        return t("Update Plate");
    }
  };

  return (
    <Modal isOpen={isOpen} size="lg">
      <ModalBackdrop />
      <ModalContent className="bg-background-100 gap-[32px] pb-0 pt-[32px] px-0 max-w-md mx-auto rounded-[16px]">
        <ModalCloseButton onPress={onClose} className="absolute right-[16px] top-[16px] z-10" data-test-id="close-button">
          <Close height={24} fill="text-typography-900" width={26} />
        </ModalCloseButton>

        <VStack className="px-[20px] gap-[16px]">
          <Box className="items-center" data-test-id="info-icon">
            {renderIcon()}
          </Box>

          <Text className="font-[700] text-[18px] text-center text-typography-900">{renderHeading()}</Text>

          <Text className="font-[400] leading-[22px] text-[16px] text-center text-typography-900">{renderMessage()}</Text>

          {type === "update" ? (
            <Box className="w-full">
              <TextInput
                className={`h-[52px] text-center bg-primary-200 text-[32px] font-semibold text-typography-900 rounded-lg p-4 ${errorMessage ? "border border-error-100" : "border border-primary-900"}`}
                onChangeText={handleSelectPlate}
                value={newPlate}
                maxLength={12}
              />
              {errorMessage && <Text className="text-xs mt-2 font-semibold text-error-100">{errorMessage}</Text>}
            </Box>
          ) : (
            <Box className="bg-primary-200 border border-primary-900 px-[16px] py-[24px] rounded-[6px]">
              <Text className="font-[700] text-[30px] text-center text-typography-900">{licensePlate}</Text>
            </Box>
          )}
        </VStack>

        <VStack className="border-t border-outline-300 flex-row gap-[10px] p-[20px] w-full">
          {type === "update" ? (
            <AppButton
              className="flex-1"
              loading={loading}
              onPress={() => {
                setLoading(true);
                onConfirm(newPlate);
              }}
              title={t("Edit Plate")}
            />
          ) : (
            <AppButton className="flex-1" onPress={handleConfirm} title={renderButtonLabel()} loading={type === "remove" ? isRemoving : loading} />
          )}
        </VStack>
      </ModalContent>
    </Modal>
  );
};

export default PlateModal;
